import { UserCell } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import ChatEmpty from "@images/empty/chat_empty.svg"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { PendingIcon } from "@images/svgIcons/pendingIcon"
import { BoxStaffItemEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffStatus, StaffColumnToSort } from "@modules/boxStaff/gateway/boxStaffDto"
import { usePersistedBoxStaffs } from "@modules/boxStaff/hooks/useBoxStaff"
import { useBoxStaffSidebar } from "@modules/boxStaff/hooks/useBoxStaffSidebar"
import { word } from "@modules/core/utils/i18n"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { HeaderTemplate } from "@modules/ui/components/dataTables/templates/headerTemplate"
import { EmptyView } from "@modules/ui/components/emptyView"
import { HuNote } from "@modules/ui/components/huNote"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useEffect, useState } from "react"

type BoxStaffTableProps = {
  setCountTab: (count: number) => void
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<BoxStaffItemEntity>()

export function BoxStaffTable({ setCountTab, className, ...props }: BoxStaffTableProps) {
  const [selectedStaffMember, setselectedStaffMember] = useState<BoxStaffItemEntity>()
  const { navigateToStaffDetailsInfo, navigateToStaffDetailsPending } = useBoxStaffSidebar()

  const {
    data,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    totalElements,
    setSorting,
    sortBy,
    sortOrder,
    isSearching,
  } = usePersistedBoxStaffs([BoxStaffStatus.ACCEPTED, BoxStaffStatus.PENDING])

  useEffect(() => {
    if (totalElements) {
      setCountTab(totalElements)
    } else setCountTab(0)
  }, [data])

  const columns = [
    columnHelper.accessor("username", {
      header: () => (
        <HeaderTemplate
          title={word("box.staff.table.label.name")}
          variant={
            sortBy === StaffColumnToSort.NAME
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(StaffColumnToSort.NAME, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(StaffColumnToSort.NAME, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: staff } }) => (
        <UserCell
          member={{
            avatar: staff.avatar,
            firstname: staff.firstname,
            lastname: staff.lastname,
          }}
          subtitle={staff.getUserName()}
          subtitleColor="neutral"
        />
      ),
    }),
    columnHelper.accessor("role", {
      header: () => (
        <HeaderTemplate
          title={word("box.staff.table.label.role")}
          variant={
            sortBy === StaffColumnToSort.ROLE
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(StaffColumnToSort.ROLE, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(StaffColumnToSort.ROLE, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: staff } }) => (
        <Text fontStyle="TS" className="text-textDefault-body">
          {displayRole(staff.role)}
        </Text>
      ),
    }),
    columnHelper.accessor("creationDate", {
      header: () => <HeaderTemplate title={word("box.staff.table.label.staffSince")} />,
      cell: ({ row: { original: staff } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {formatDateWithHyphen(dayjs(staff.creationDate).format("L"))}
        </Text>
      ),
    }),
    columnHelper.accessor("status", {
      header: () => <HeaderTemplate title={word("box.staff.table.label.status")} />,
      cell: ({ row: { original: staff } }) => {
        if (staff.status === BoxStaffStatus.ACCEPTED) {
          return (
            <Text fontStyle="BM" className="flex gap-2 text-textDefault-body">
              <CheckmarkIcon
                color="var(--color-custom-green-500)"
                style={{
                  width: "24px",
                  height: "24px",
                }}
              />{" "}
              {word("box.staff.table.status.active")}
            </Text>
          )
        } else if (staff.status === BoxStaffStatus.PENDING) {
          return (
            <Text fontStyle="BM" className="flex gap-2 text-textDefault-body">
              <PendingIcon
                color="var(--color-custom-yellow-500)"
                style={{
                  width: "24px",
                  height: "24px",
                }}
              />{" "}
              {word("box.staff.table.status.pending")}
            </Text>
          )
        } else return <></>
      },
    }),
    columnHelper.accessor("internalNote", {
      header: () => <HeaderTemplate title={word("box.member.table.note")} />,
      cell: ({ row: { original: staff } }) => (
        <HuNote id={staff.id} index={staff.id} note={staff.internalNote ? staff.internalNote : ""} />
      ),
    }),
  ]

  const onSelectedStaffMember = (e: BoxStaffItemEntity) => {
    if (e.status === BoxStaffStatus.PENDING) {
      navigateToStaffDetailsPending(e.id, {
        onHide: () => {
          setselectedStaffMember(undefined)
          return Promise.resolve(true)
        },
        replaceAllComponents: true,
      })
    } else {
      navigateToStaffDetailsInfo(e.id, {
        onHide: () => {
          setselectedStaffMember(undefined)
          return Promise.resolve(true)
        },
        replaceAllComponents: true,
      })
    }
    setselectedStaffMember(e)
  }

  if (data.length === 0)
    return (
      <EmptyView
        image={{ src: ChatEmpty }}
        title={isSearching ? word("searching.list.empty.title") : word("member.list.empty.title")}
        description={isSearching ? word("searching.list.empty.description") : word("member.list.empty.description")}
        paddingType="In_table"
      />
    )

  return (
    <div className={cn("flex flex-col px-6", className)} {...props}>
      <HUDatatable
        data={data}
        columns={columns}
        pageSize={pageSize}
        setPageSize={setPageSize}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handleRowSelectionChange={(row: BoxStaffItemEntity) => onSelectedStaffMember(row)}
        selectedRowIndex={data.findIndex((item) => item.id === selectedStaffMember?.id)}
        totalElements={totalElements}
      />
    </div>
  )
}
